import { GridFilterItem } from '@mui/x-data-grid-pro';

import { httpClient, httpGet, httpPost, HttpMethod } from './http-client';
import {
  ActionType,
  AllocatedService,
  BillingType,
  BulkEditServiceDataType,
  EstimatedTimeOfArrivalQuery,
  LinkedServiceData,
  NextServiceStatusesResponse,
  OtherServiceListData,
  ProcessType,
  ServiceAccordianData,
  ServiceAttribute,
  ServiceBillingInfo,
  ServiceCommonType,
  ServiceCreationObject,
  ServiceListResponse,
  ServiceLog,
  ServiceLogHideData,
  ServiceLogResponse,
  ServiceNodeAllocationData,
  ServiceObject,
  ServiceStatusResponse,
  ServiceTargetResponse,
  ServiceTemplateResponse,
  // ServiceTypeResponse, // TODO: unused - kept for reference
  ServiceUpdateRequest,
  ServicesNodesAttributes,
  ServiceActivityResponseDataType,
  ServiceAttendanceObject,
} from '../@types/service.type';
import { ColumnSelectionItem } from '../components/templates/ColumnSelection';
import { mapNumericEqualsOperatorForWire } from '../utils/filterModel';

export interface ServiceQueryOptions {
  filterModel: { items?: GridFilterItem[] };
  page: number;
  pageSize: number;
  sortModel?: ReadonlyArray<{ field: string; sort: string | null | undefined }>;
  locationId?: number | string | null;
  includeChildRecords?: boolean | string | null;
  isFromStructures?: boolean | string | null;
  defaultReport?: string;
  multiSearchQuery?: string;
}

export interface ServiceStatusChangeRequest {
  status: string;
  notes?: string;
}

export interface ParentServiceUpdateRequest {
  parentServiceId: number | null;
}

export interface BulkServiceIdsRequest {
  affectedServiceIds: number[];
}

export interface ServiceBulkCreateRequest {
  service: ServiceCreationObject;
  tag?: {
    entityType: string;
    entityId: number;
    freshTags: string[];
    existingTags: number[];
  };
}

export const createService = (service: ServiceCreationObject): Promise<ServiceObject> => {
  return httpClient<ServiceObject>('/services', HttpMethod.Post, service);
};

export const getActionTypes = (signal?: AbortSignal): Promise<ActionType[]> => {
  return httpGet<ActionType[]>('/actionTypes', signal);
};

export const getProcessTypes = (signal?: AbortSignal): Promise<ProcessType[]> => {
  return httpGet<ProcessType[]>('/processTypes', signal);
};

export const getServiceActionProcessTypes = (
  activeOnly?: boolean,
  signal?: AbortSignal
): Promise<ServiceCommonType> => {
  return httpGet<ServiceCommonType>(
    `/service/action/process/types?activeOnly=${activeOnly}`,
    signal
  );
};

export const createServiceLogsForService = (
  serviceId: number,
  service: ServiceLog
): Promise<ServiceLogResponse> => {
  return httpClient<ServiceLogResponse>(
    `/serviceLogs/${serviceId}/forService`,
    HttpMethod.Post,
    service
  );
};

export const updateServiceLogsById = (
  id: number,
  serviceLog: Partial<ServiceLog> & { serviceId?: number; isHidden?: boolean }
): Promise<ServiceLogResponse> => {
  return httpClient<ServiceLogResponse>(`/serviceLogs/${id}`, HttpMethod.Put, serviceLog);
};

export const getAllServiceTargets = (signal?: AbortSignal): Promise<ServiceTargetResponse[]> => {
  return httpGet<ServiceTargetResponse[]>('/serviceTargets', signal);
};

export const getServiceById = (id: number, signal?: AbortSignal): Promise<ServiceObject> => {
  return httpGet<ServiceObject>(`/services/${id}`, signal);
};

export const updateServiceById = (
  id: number,
  data: ServiceUpdateRequest
): Promise<ServiceObject> => {
  return httpClient<ServiceObject>(`/services/${id}`, HttpMethod.Put, data);
};

/**
 * PUT /services/:id is a full-record update: the bff-web handler unconditionally
 * force-nulls `scheduledAt`, `followUpAt`, and `subLocationNodeId` whenever they
 * are absent from the request body (serviceRoutes.ts general-edit block:
 * `service_result.followUpAt = req.body?.followUpAt || null`, and likewise for
 * `scheduledAt`/`subLocationNodeId`). Any *partial* update must therefore echo
 * the current values of these fields, otherwise saving one of them silently
 * wipes the others. Spread the result into the payload first, then apply the
 * field(s) you are actually changing so they take precedence.
 */
export const preservedServiceFields = (
  service: Pick<ServiceObject, 'scheduledAt' | 'followUpAt' | 'subLocationNodeId'>
): Pick<ServiceUpdateRequest, 'scheduledAt' | 'followUpAt' | 'subLocationNodeId'> => ({
  scheduledAt: service.scheduledAt || undefined,
  followUpAt: service.followUpAt || undefined,
  subLocationNodeId: service.subLocationNodeId ?? undefined,
});

// Reported information update only needs these fields
export interface ReportedInfoUpdateRequest {
  referenceNo1?: string;
  referenceNo2?: string;
  referenceNo3?: string;
  reportedSerialNumber?: number;
  reportedFaultAndDescriptionId?: number;
  reportedModelId?: number;
}

export const updateReportedInformationByServiceId = (
  id: number,
  data: ReportedInfoUpdateRequest
): Promise<ServiceObject> => {
  return httpClient<ServiceObject>(`/services/reportedInformation/${id}`, HttpMethod.Put, data);
};

// ID fields the frontend always needs for row-level hyperlinks, independent of
// the user's visible column selection. Injected into the BFF request so the
// response contains them even when the matching columns are hidden.
const REQUIRED_ROW_ID_FIELDS = ['primaryAllocationNodeId', 'structureNodeId'] as const;

// Frontend-only columns derived from raw BFF fields. When a user selects one,
// we force-select its source and strip the derived entry from the outgoing
// payload so the BFF never sees a field it doesn't recognise.
const DERIVED_COLUMN_SOURCES: Record<string, string> = {
  clientSlaStatus: 'clientSLASummary',
  opsSlaStatus: 'opsSLASummary',
};

// The BFF's `defaultReport` code path keys field projection by section title.
// Section titles double as user-facing labels in the column-selection UI, so
// the "Service" → "Job" copy change in a05581a leaked into the wire payload
// and caused the BFF to silently drop every column under that section
// (including clientHierarchy, contractName, logoPic, priorityCode, …). Rewrite
// the affected section title back to the BFF-known value before sending.
const WIRE_SECTION_TITLES: Record<string, string> = {
  Job: 'Service',
};

// Fields the BFF only populates when another field is also requested. The
// /servicesList response joins the contract logo through the client column,
// so asking for `logoPic` without `clientHierarchy` returns empty cells.
// Mobile already requests `clientHierarchy`, which is why the same payload
// works there. Force the dependency at the wire boundary so the desktop
// column picker doesn't have to know about the join.
const CO_REQUIRED_FIELDS: Record<string, readonly string[]> = {
  logoPic: ['clientHierarchy'],
};

export const prepareColumnSelectionForBFF = (
  items: ColumnSelectionItem[]
): ColumnSelectionItem[] => {
  if (items.length === 0) return items;

  const missing = new Set<string>(REQUIRED_ROW_ID_FIELDS);
  const sourcesToForce = new Set<string>();

  for (const group of items) {
    for (const col of group.value) {
      if (col.value && DERIVED_COLUMN_SOURCES[col.field]) {
        sourcesToForce.add(DERIVED_COLUMN_SOURCES[col.field]);
      }
      // Route deps through `missing` rather than `sourcesToForce` so a custom
      // column-selection payload that omits the dep entirely still gets it
      // injected via the extras fallback below.
      if (col.value && CO_REQUIRED_FIELDS[col.field]) {
        for (const dep of CO_REQUIRED_FIELDS[col.field]) missing.add(dep);
      }
    }
  }

  const updated = items.map((group) => ({
    ...group,
    title: WIRE_SECTION_TITLES[group.title] ?? group.title,
    value: group.value.flatMap((col) => {
      if (DERIVED_COLUMN_SOURCES[col.field]) return [];
      if (missing.has(col.field)) {
        missing.delete(col.field);
        return [{ ...col, value: true }];
      }
      if (sourcesToForce.has(col.field) && !col.value) {
        return [{ ...col, value: true }];
      }
      return [col];
    }),
  }));

  if (missing.size === 0) return updated;
  const extras = Array.from(missing).map((field) => ({ title: field, field, value: true }));
  return updated.map((group, index) =>
    index === 0 ? { ...group, value: [...group.value, ...extras] } : group
  );
};

/**
 * Services-list filter fields that map to *numeric* columns in api-services
 * (`se.id` and friends). Mirrors `isNumericalColumn` in
 * api-services/src/utils/sequelizeOperations.ts — keep the two in sync.
 */
const NUMERIC_FILTER_FIELDS: ReadonlySet<string> = new Set([
  'serviceId',
  'structureNodeId',
  'subLocationId',
  'partTypeId',
  'daysOpen',
  'attendanceCount',
  'reopensCount',
  'uniqueTechniciansCount',
]);

/**
 * Translate a filter operator to the form the services BFF understands for a
 * given field before it goes on the wire.
 *
 * Why: api-services' `mapOperatorToRawSQL` turns `equals` into a SQL `ILIKE`
 * for *every* column. On the numeric columns above (`se.id` etc.) `integer
 * ILIKE text` is invalid, so the query errors and the filter returns nothing
 * — this is why the "Job ID" custom filter never filtered. The literal `=`
 * operator is the one api-services maps to a plain numeric comparison, so we
 * rewrite `equals` → `=` for these fields only. All other operators (the
 * numeric-safe `isAnyOf`, comparison operators, `isEmpty`, …) pass through
 * unchanged, as do operators on non-numeric fields.
 *
 * `contains` (a true substring match) has no numeric-safe equivalent on the
 * current BFF — it needs api-services to cast the id column to text — so it is
 * deliberately left untouched (and still non-functional) until that backend
 * change lands. Tracked in the backlog.
 */
export const mapServiceFilterOperatorForWire = (
  field: string | undefined,
  operator: string | undefined
): string | undefined => mapNumericEqualsOperatorForWire(field, operator, NUMERIC_FILTER_FIELDS);

export const getAllServices = (
  queryOptions: ServiceQueryOptions,
  columnSelectionItems: ColumnSelectionItem[],
  signal?: AbortSignal
): Promise<ServiceListResponse> => {
  const {
    filterModel,
    page,
    pageSize,
    sortModel,
    locationId,
    includeChildRecords,
    defaultReport,
    multiSearchQuery,
  } = queryOptions;
  let url = `/servicesList?page=${page + 1}&limit=${pageSize}`;

  if (sortModel && sortModel[0] && sortModel[0].field) {
    url += `&order_by=${sortModel[0].field}&order_direction=${sortModel[0].sort}`;
  }

  filterModel.items?.forEach((filter: GridFilterItem) => {
    const field = filter?.field;
    const operator = mapServiceFilterOperatorForWire(field, filter?.operator);
    const value = filter?.value ?? '';
    url += `&${field}=${operator},${value}`;
  });

  if (locationId) {
    url += `&locationId=${locationId}`;
  }

  if (includeChildRecords) {
    url += `&includeChildRecords=${includeChildRecords}`;
  }

  if (defaultReport) {
    url += `&defaultReport=${defaultReport}`;
  }

  if (multiSearchQuery) {
    url += `&multiSearchQuery=${multiSearchQuery}`;
  }

  return httpPost<ServiceListResponse>(
    url,
    { columnSelectionItems: prepareColumnSelectionForBFF(columnSelectionItems) },
    signal
  );
};

export const getServiceLogsByServiceId = (
  id: number,
  signal?: AbortSignal
): Promise<ServiceLogResponse[]> => {
  return httpGet<ServiceLogResponse[]>(`/services/${id}/logs`, signal);
};

export const getServiceLogsById = (
  id: number,
  signal?: AbortSignal
): Promise<ServiceLogResponse> => {
  return httpGet<ServiceLogResponse>(`/serviceLogs/${id}`, signal);
};

export const hideServiceLog = (id: number, nodeType: ServiceLogHideData): Promise<void> => {
  return httpClient<void>(`/servicelogs/${id}/hide`, HttpMethod.Put, nodeType);
};

export const updateBulkServiceList = (serviceType: BulkEditServiceDataType): Promise<void> => {
  return httpClient<void>('/serviceListBulkUpdate', HttpMethod.Patch, serviceType);
};

export const updateBulkServiceEditList = (serviceType: BulkEditServiceDataType): Promise<void> => {
  return httpClient<void>('/serviceEditListBulkUpdate', HttpMethod.Patch, serviceType);
};

export const getBulkServicesByIds = (data: BulkServiceIdsRequest): Promise<ServiceObject[]> => {
  return httpClient<ServiceObject[]>('/services/bulkservicedetails', HttpMethod.Post, data);
};

export const getNextServiceStatuses = (
  code: string,
  serviceType: string,
  serviceId: number,
  signal?: AbortSignal
): Promise<NextServiceStatusesResponse> => {
  return httpGet<NextServiceStatusesResponse>(
    `/possibleNextserviceStatuses/${code}/${serviceType}?serviceId=${serviceId}`,
    signal
  );
};

export const saveServiceStatus = (
  id: number,
  status: ServiceStatusChangeRequest
): Promise<ServiceObject> => {
  return httpClient<ServiceObject>(`/services/${id}/status`, HttpMethod.Put, status);
};

export const getServiceTemplates = (signal?: AbortSignal): Promise<ServiceTemplateResponse[]> => {
  return httpGet<ServiceTemplateResponse[]>('/servicetemplates', signal);
};

export const getOtherServicesUnderMainLocation = (
  nodeId: number,
  queryOptions: { page: number; pageSize: number },
  signal?: AbortSignal
): Promise<OtherServiceListData> => {
  const { page, pageSize } = queryOptions;
  const url = `/nodes/${nodeId}/otherservices?page=${page + 1}&limit=${pageSize}`;
  return httpGet<OtherServiceListData>(url, signal);
};

export const getLinkedServices = (
  serviceId: number,
  signal?: AbortSignal
): Promise<LinkedServiceData> => {
  return httpGet<LinkedServiceData>(`/services/getlinkedservicesbyserviceid/${serviceId}`, signal);
};

export const updateParentService = (
  id: number,
  parentService: ParentServiceUpdateRequest
): Promise<ServiceObject> => {
  return httpClient<ServiceObject>(`/services/parentservice/${id}`, HttpMethod.Put, parentService);
};

export const getAllServiceAttendance = (
  serviceId: number,
  signal?: AbortSignal
): Promise<ServiceAttendanceObject[]> => {
  return httpGet<ServiceAttendanceObject[]>(`/attendance/${serviceId}`, signal);
};

export const getCurrentUserAllocatedServices = (
  locationNodeId: number,
  serviceId: number,
  signal?: AbortSignal
): Promise<AllocatedService> => {
  return httpGet<AllocatedService>(
    `/services/technicians/${locationNodeId}/allocation/${serviceId}`,
    signal
  );
};

export const updateBillingInfo = (
  serviceId: number,
  billing: ServiceBillingInfo
): Promise<void> => {
  return httpClient<void>(`/services/billingInfo/${serviceId}`, HttpMethod.Put, billing);
};

export const getServicesUnderNodeId = (
  nodeId: number,
  includeChildRecords: boolean,
  signal?: AbortSignal
): Promise<ServiceAccordianData[]> => {
  const url = `/nodes/${nodeId}/services?includeChildRecords=${includeChildRecords}`;
  return httpGet<ServiceAccordianData[]>(url, signal);
};

export const updateServiceNodeAllocation = (data: ServiceNodeAllocationData): Promise<void> => {
  return httpClient<void>('/servicenodeallocation', HttpMethod.Post, data);
};

export const getEstimatedTimeOfArrival = (
  queryOptions: EstimatedTimeOfArrivalQuery,
  signal?: AbortSignal
): Promise<string> => {
  const { nodeId, origin, date, serviceId } = queryOptions;
  const url = `/getEstimatedTimeOfArrival/${nodeId}/${serviceId}/data?origin=${origin}&date=${date}`;
  return httpGet<string>(url, signal);
};

export const getServicesNodesLocationRelatedAttributes = (
  serviceId: number,
  mainLocationNodeId: number,
  subLocationNodeId: number,
  signal?: AbortSignal
): Promise<ServicesNodesAttributes> => {
  const url = `/serviceAttributes/${serviceId}/nodeAttributes/mainLocation/${mainLocationNodeId}/subLocation/${subLocationNodeId}`;
  return httpGet<ServicesNodesAttributes>(url, signal);
};

export const createOrUpdateServiceAttributes = (
  serviceAttribute: ServiceAttribute
): Promise<void> => {
  return httpClient<void>(
    '/serviceAttributesBulkCreateOrUpdate',
    HttpMethod.Post,
    serviceAttribute
  );
};

export const getBillingTypes = (signal?: AbortSignal): Promise<BillingType[]> => {
  return httpGet<BillingType[]>('/billingTypes', signal);
};

// BFF `/getServiceCloseOrReOpenStatuses` returns a bare array of statuses, not
// the `{ possibleNextStatuses, preferedStatuses }` shape used by the regular
// next-statuses endpoint.
export const getServiceCancelOrReopenStatuses = (
  option: string,
  statusCode: string,
  serviceType: string,
  serviceId: number,
  signal?: AbortSignal
): Promise<ServiceStatusResponse[]> => {
  const url = `/getServiceCloseOrReOpenStatuses?option=${option}&statusCode=${statusCode}&serviceType=${serviceType}&serviceId=${serviceId}`;
  return httpGet<ServiceStatusResponse[]>(url, signal);
};

export const getBillingInfo = (
  serviceId: number,
  signal?: AbortSignal
): Promise<ServiceBillingInfo> => {
  return httpGet<ServiceBillingInfo>(`/services/billingInfo/${serviceId}`, signal);
};

export const getServiceActivityMonitorData = (
  signal?: AbortSignal
): Promise<ServiceActivityResponseDataType[]> => {
  return httpGet<ServiceActivityResponseDataType[]>('/getServiceActivityMonitor', signal);
};

export const createServiceBulk = (
  serviceBulkObj: ServiceBulkCreateRequest
): Promise<ServiceObject[]> => {
  return httpClient<ServiceObject[]>('/services/bulk', HttpMethod.Post, serviceBulkObj);
};
